import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Google Fonts available options
export const GOOGLE_FONTS = [
  { name: 'Inter', value: 'Inter', category: 'sans-serif' },
  { name: 'Roboto', value: 'Roboto', category: 'sans-serif' },
  { name: 'Open Sans', value: 'Open Sans', category: 'sans-serif' },
  { name: 'Lato', value: 'Lato', category: 'sans-serif' },
  { name: 'Montserrat', value: 'Montserrat', category: 'sans-serif' },
  { name: 'Poppins', value: 'Poppins', category: 'sans-serif' },
  { name: 'Source Sans Pro', value: 'Source Sans 3', category: 'sans-serif' },
  { name: 'Nunito', value: 'Nunito', category: 'sans-serif' },
  { name: 'Raleway', value: 'Raleway', category: 'sans-serif' },
  { name: 'Work Sans', value: 'Work Sans', category: 'sans-serif' },
  { name: 'Playfair Display', value: 'Playfair Display', category: 'serif' },
  { name: 'Merriweather', value: 'Merriweather', category: 'serif' },
  { name: 'Lora', value: 'Lora', category: 'serif' },
  { name: 'PT Serif', value: 'PT Serif', category: 'serif' },
  { name: 'Fira Code', value: 'Fira Code', category: 'monospace' },
  { name: 'JetBrains Mono', value: 'JetBrains Mono', category: 'monospace' }
];

export const ThemeProvider = ({ children }) => {
  // Theme mode (light/dark)
  const [mode, setMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme-mode') || 'light';
    }
    return 'light';
  });

  // Font settings
  const [fonts, setFonts] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('theme-fonts');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          return { heading: 'Inter', body: 'Inter' };
        }
      }
    }
    return { heading: 'Inter', body: 'Inter' };
  });

  // Load Google Fonts
  useEffect(() => {
    const loadFonts = () => {
      const fontsToLoad = new Set([fonts.heading, fonts.body]);
      const fontString = Array.from(fontsToLoad)
        .map(f => f.replace(/ /g, '+') + ':wght@300;400;500;600;700')
        .join('&family=');
      
      // Remove existing Google Fonts link
      const existingLink = document.getElementById('google-fonts-link');
      if (existingLink) {
        existingLink.remove();
      }

      // Add new Google Fonts link
      const link = document.createElement('link');
      link.id = 'google-fonts-link';
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${fontString}&display=swap`;
      document.head.appendChild(link);

      // Apply fonts to CSS variables
      document.documentElement.style.setProperty('--font-heading', `"${fonts.heading}", sans-serif`);
      document.documentElement.style.setProperty('--font-body', `"${fonts.body}", sans-serif`);
    };

    loadFonts();
  }, [fonts]);

  // Apply theme mode
  useEffect(() => {
    const root = document.documentElement;
    
    if (mode === 'dark') {
      root.classList.add('dark');
      root.style.setProperty('--bg-primary', '#0f172a');
      root.style.setProperty('--bg-secondary', '#1e293b');
      root.style.setProperty('--bg-card', '#1e293b');
      root.style.setProperty('--text-primary', '#f8fafc');
      root.style.setProperty('--text-secondary', '#94a3b8');
      root.style.setProperty('--border-color', '#334155');
      root.style.setProperty('--main-bg', '#0f172a');
    } else {
      root.classList.remove('dark');
      root.style.setProperty('--bg-primary', '#ffffff');
      root.style.setProperty('--bg-secondary', '#f8fafc');
      root.style.setProperty('--bg-card', '#ffffff');
      root.style.setProperty('--text-primary', '#0f172a');
      root.style.setProperty('--text-secondary', '#64748b');
      root.style.setProperty('--border-color', '#e2e8f0');
      root.style.setProperty('--main-bg', 'linear-gradient(135deg, #f8fafc, white, rgba(20, 184, 166, 0.03))');
    }

    localStorage.setItem('theme-mode', mode);
  }, [mode]);

  // Save fonts to localStorage
  useEffect(() => {
    localStorage.setItem('theme-fonts', JSON.stringify(fonts));
  }, [fonts]);

  const toggleMode = () => {
    setMode(prev => prev === 'light' ? 'dark' : 'light');
  };

  const setHeadingFont = (font) => {
    setFonts(prev => ({ ...prev, heading: font }));
  };

  const setBodyFont = (font) => {
    setFonts(prev => ({ ...prev, body: font }));
  };

  // Calculate isDark here to ensure it updates with mode
  const isDark = mode === 'dark';

  const value = {
    mode,
    setMode,
    toggleMode,
    isDark,
    fonts,
    setFonts,
    setHeadingFont,
    setBodyFont
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
